import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useNotify } from '~/hooks/useNotify'
import { useInvalidateSurveyForSettingsQuery } from '~/hooks/useSurveyForSettingsQuery'
import { ensureExists, exists } from '~/utils'

import { useInvalidateMainQuery } from '../hooks/useMainQuery'

const document = graphql(`
  query DataSettingsPage_GetUploadedFileResult($awaitId: String!) {
    getUploadedFileResult(awaitId: $awaitId) {
      arrived
      error
      result {
        surveyId
      }
    }
  }
`)

export const useObserveUploadedFile = (
  awaitId: string | undefined,
  onCompleted: () => void,
) => {
  const { t } = useTranslation()
  const graphQLRequest = useGraphQLRequest()
  const getUploadedQuestionFileResultQuery = useQuery({
    queryFn: () =>
      graphQLRequest({
        document,
        variables: { awaitId: ensureExists(awaitId, t('assertion_failed')) },
      }),
    queryKey: ['DataSettingsPage_GetUploadedFileResult', awaitId],
    enabled: exists(awaitId),
    refetchInterval: 1000,
  })
  const notify = useNotify()
  const invalidateSurveyForSettingsQuery = useInvalidateSurveyForSettingsQuery()
  const invalidateMainQuery = useInvalidateMainQuery()
  React.useEffect(() => {
    const getUploadedFileResult =
      getUploadedQuestionFileResultQuery.data?.getUploadedFileResult

    const arrived = getUploadedFileResult?.arrived ?? false
    if (arrived) {
      onCompleted()
      // The engine's raw message stays on `result.error`; the backend puts its
      // translated copy on the outer `error`, so that is the only one shown.
      const error = getUploadedFileResult?.error
      if (!exists(error)) {
        notify({
          content: t('data_uploaded_successfully'),
          style: 'success',
        })
        invalidateSurveyForSettingsQuery()
        invalidateMainQuery()
      } else {
        notify({ content: error, style: 'error' })
      }
    }
  }, [
    getUploadedQuestionFileResultQuery.data?.getUploadedFileResult,
    invalidateMainQuery,
    invalidateSurveyForSettingsQuery,
    notify,
    t,
    onCompleted,
  ])
}
